import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";
import SinConexionKawaii from "../SinConexionKawaii";
import ImagenNoDisponibleKawaii from "../ImagenNoDisponibleKawaii";

// Template52: Página de Ueno con mapa de Tokio
// Copia DIRECTA del componente Page9.jsx de guia-tokio, adaptado para usar las props del sistema guia
const Template52 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	onMapFullscreenChange,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [loaded, setLoaded] = useState(navigator.onLine);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(navigator.onLine);
	const [uenoImageError, setUenoImageError] = useState(false);
	const [ameyayokochoImageError, setAmeyayokochoImageError] = useState(false);
	const [uenoStickerError, setUenoStickerError] = useState(false);
	const [uenoSticker2Error, setUenoSticker2Error] = useState(false);

	// Datos hardcodeados de Ueno (valores por defecto)
	const headerText = page.texts?.[0]?.content;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;

	// Lógica especial para la guía de Tokio: header #24221a en primera página, #e24217 en las siguientes
	const pageOrder =
		page.order !== undefined
			? page.order
			: page.data?.order !== undefined
				? page.data.order
				: -1;
	const isTokioGuide = headerBackgroundColor === "#24221a";
	const actualHeaderColor =
		isTokioGuide && pageOrder > 0 ? "#e24217" : headerBackgroundColor;

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const uenoImage = getImageSrc("ueno");
	const ameyayokochoImage = getImageSrc("ameyayokocho");
	const uenoSticker = getCanvasImageUrl("ueno-sticker");
	const uenoSticker2 = getCanvasImageUrl("ueno-sticker-2");

	// Textos desde page.texts
	const introText = page.texts?.[1]?.content;
	const uenoTitle = page.texts?.[2]?.content;
	const uenoText = page.texts?.[3]?.content;
	const ameyokoTitle = page.texts?.[4]?.content;
	const ameyokoText = page.texts?.[5]?.content;
	const sabiasQueText = page.texts?.[6]?.content;
	const stickerMapaText = page.texts?.[7]?.content;
	const dentroParqueTitle = page.texts?.[8]?.content;
	const dentroParqueText = page.texts?.[9]?.content;
	const sabiasQueAmeyokoText = page.texts?.[10]?.content;

	// Mapa desde page.maps
	const mapUrl = page.maps?.[0];

	useEffect(() => {
		const handleOnline = () => {
			setIsOnline(true);
			setLoaded(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
			setLoaded(true);
		};

		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);

		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	useEffect(() => {
		const style = document.createElement("style");
		style.innerHTML = `
			@keyframes spin {
				0% { transform: rotate(0deg); }
				100% { transform: rotate(360deg); }
			}
		`;
		document.head.appendChild(style);
		return () => document.head.removeChild(style);
	}, []);

	// Parsear el texto de SabiasQue para extraer los iconos y textos
	const parseSabiasQue = (htmlText) => {
		if (!htmlText) return [];
		const parser = new DOMParser();
		const doc = parser.parseFromString(htmlText, "text/html");
		const paragraphs = doc.querySelectorAll("p");
		const items = [];
		paragraphs.forEach((p) => {
			const text = p.textContent ?? p.innerText;
			const emojiMatch = text.match(/^([^\s]+)\s/);
			if (emojiMatch) {
				items.push({
					icono: emojiMatch[1],
					texto: text.substring(emojiMatch[1].length + 1).trim(),
				});
			}
		});
		return items;
	};

	const sabiasQueItems = parseSabiasQue(sabiasQueText);
	const sabiasQueAmeyokoItems = parseSabiasQue(sabiasQueAmeyokoText);

	const contentColor = textColor || "#444";

	// Estilos reutilizables
	const estiloH3 = {
		fontFamily: `${fontFamily} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const estiloP = {
		fontFamily: `${fontFamily} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	if (fullscreen) {
		// Vista de mapa grande
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: "#24221a",
					}}
				>
					<Box
						component="button"
						onClick={() => {
									setFullscreen(false);
									onMapFullscreenChange?.(false);
								}}
						sx={{
							backgroundColor: "#fff",
							color: "#24221a",
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: `${fontFamily}`,
						}}
					>
						← Volver a la guía
					</Box>
				</Box>
				{isOnline && mapUrl ? (
					<Box
						component="iframe"
						title="Mapa grande"
						src={mapUrl}
						width="100%"
						height="100%"
						sx={{
							border: 0,
							flex: 1,
						}}
						allowFullScreen
						loading="lazy"
					/>
				) : (
					<Box
						sx={{
							flex: 1,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<SinConexionKawaii height="100%" fontFamily={fontFamily} />
					</Box>
				)}
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor,
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily} !important`,
				"& *:not([data-template-header]):not([data-map-button])": {
					fontFamily: `${fontFamily} !important`,
					color: "inherit",
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "10px !important",
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor,
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily}`,
					fontSize: "14px",
					fontWeight: 800,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1053,
				}}
			>
				{headerText}
				{!isSmallScreen && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{/* MAPA */}
			<Box data-block-id="map" sx={{ flex: 1, marginTop: "-70px", position: "relative" }}>
				{!loaded && isOnline && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							height: "100%",
							color: "black",
							fontFamily: `${fontFamily}`,
							fontSize: 14,
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 1,
							marginTop: "30px",
						}}
					>
						<Box
							sx={{
								width: 20,
								height: 20,
								border: "4px solid #ccc",
								borderTop: "4px solid #e24217",
								borderRadius: "50%",
								animation: "spin 1s linear infinite",
							}}
						/>
					</Box>
				)}

				{isOnline ? (
					<Box
						component="iframe"
						title="Mapa Asakusa/Ueno"
						src={mapUrl}
						width="100%"
						height={isSmallScreen ? "400px" : "340px"}
						allowFullScreen=""
						loading="lazy"
						sx={{
							border: 0,
						}}
						onLoad={() => setLoaded(true)}
						onError={() => {
							setLoaded(true);
							setIsOnline(false);
						}}
					/>
				) : (
					<SinConexionKawaii
						height={"100%"}
						fontFamily={fontFamily}
						message="Sin conexión"
						submessage="El mapa se cargará cuando tengas conexión"
					/>
				)}

				{isOnline && (
					<Box
						component="button"
						data-map-button
						onClick={() => {
									setFullscreen(true);
									onMapFullscreenChange?.(true);
								}}
						sx={{
							position: "absolute",
							top: "80px",
							right: "10px",
							backgroundColor: "#24221a",
							color: "#fff",
							padding: "8px 10px",
							fontSize: "11px",
							borderRadius: "8px",
							textDecoration: "none",
							fontFamily: `${fontFamily}`,
							boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",
							zIndex: 4,
							border: "none",
							cursor: "pointer",
						}}
					>
						Ver mapa grande
					</Box>
				)}
				<Box
					sx={{
						position: "absolute",
						bottom: "80px",
						right: "5px",
						backgroundColor: "#fff8db",
						border: "2px solid #f5c400",
						borderRadius: "16px 16px 0px 16px",
						padding: "10px 12px",
						fontSize: 11,
						fontFamily: `${fontFamily} !important`,
						color: contentColor,
						boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
						maxWidth: 120,
						zIndex: 3,
					}}
				>
					{stickerMapaText}
				</Box>
				{uenoSticker && !uenoStickerError && (
					<img
						src={uenoSticker}
						alt=""
						onError={() => setUenoStickerError(true)}
						style={{
							width: 110,
							position: "absolute",
							bottom: 0,
							right: -10,
							zIndex: 2,
						}}
					/>
				)}
			</Box>

			{/* CONTENIDO */}
			<Box sx={{ padding: "10px 12px", position: "relative" }}>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: "4px",
					}}
				>
					<Box
						data-block-id="text-1"
						component="div"
						dangerouslySetInnerHTML={{ __html: introText }}
						sx={{
							fontFamily: `${fontFamily} !important`,
							fontSize: 13,
							color: "#333",
							margin: 0,
							padding: 0,
						}}
					/>

					{/* Parque de Ueno */}
					<Box data-block-id="text-2" sx={{ display: "flex", gap: "16px", position: "relative" }}>
						{uenoImage && !uenoImageError ? (
							<img
								src={uenoImage}
								alt="Ueno"
								onError={() => setUenoImageError(true)}
								style={{
									width: "40%",
									height: 110,
									objectFit: "cover",
									borderRadius: "12px",
								}}
							/>
						) : (
							<ImagenNoDisponibleKawaii
								width="40%"
								height={110}
								fontFamily={fontFamily}
								message="Imagen no disponible"
								compact
							/>
						)}
						<Box sx={{ width: "60%" }}>
							<Box component="h3" sx={estiloH3}>
								{uenoTitle}
							</Box>
							<Box
								component="div"
								dangerouslySetInnerHTML={{ __html: uenoText }}
								sx={{
									...estiloP,
									fontFamily: `${fontFamily} !important`,
								}}
							/>
						</Box>
						{sabiasQueItems.length > 0 && (
							<>
								{sabiasQueItems[0] && (
									<Box
										sx={{
											position: "absolute",
											top: isSmallScreen ? 16 : -6,
											left: "33%",
										}}
									>
										<SabiasQue
											icono={sabiasQueItems[0].icono}
											texto={sabiasQueItems[0].texto}
										/>
									</Box>
								)}
								{sabiasQueItems[1] && (
									<Box
										sx={{
											position: "absolute",
											top: isSmallScreen ? 55 : 35,
											left: "33%",
										}}
									>
										<SabiasQue
											icono={sabiasQueItems[1].icono}
											texto={sabiasQueItems[1].texto}
										/>
									</Box>
								)}
							</>
						)}
					</Box>

					{/* Dentro del parque */}
					<Box data-block-id="text-3" sx={{ display: "flex", gap: "16px", position: "relative" }}>
						<Box sx={{ width: "100%" }}>
							<Box
								component="p"
								sx={{
									...estiloP,
									padding: "12px",
									backgroundColor: "#ffe3cc",
									borderRadius: "12px",
									fontFamily: `${fontFamily} !important`,
								}}
							>
								<Box component="h3" sx={estiloH3}>
									{dentroParqueTitle}
								</Box>
								<Box dangerouslySetInnerHTML={{ __html: dentroParqueText }} />
							</Box>
						</Box>
					</Box>

					{/* Mercado Ameyoko */}
					<Box data-block-id="text-4" sx={{ display: "flex", gap: "16px", position: "relative" }}>
						{ameyayokochoImage && !ameyayokochoImageError ? (
							<img
								src={ameyayokochoImage}
								alt="Ameyayokocho"
								onError={() => setAmeyayokochoImageError(true)}
								style={{
									width: "40%",
									height: 110,
									objectFit: "cover",
									borderRadius: "12px",
								}}
							/>
						) : (
							<ImagenNoDisponibleKawaii
								width="40%"
								height={110}
								fontFamily={fontFamily}
								message="Imagen no disponible"
								compact
							/>
						)}
						<Box sx={{ width: "60%" }}>
							<Box component="h3" sx={estiloH3}>
								{ameyokoTitle}
							</Box>
							<Box
								component="div"
								dangerouslySetInnerHTML={{ __html: ameyokoText }}
								sx={{
									...estiloP,
									fontFamily: `${fontFamily} !important`,
								}}
							/>
						</Box>
						{sabiasQueAmeyokoItems.length > 0 && (
							<>
								{sabiasQueAmeyokoItems[0] && (
									<Box
										sx={{
											position: "absolute",
											top: isSmallScreen ? 16 : -6,
											left: "33%",
										}}
									>
										<SabiasQue
											icono={sabiasQueAmeyokoItems[0].icono}
											texto={sabiasQueAmeyokoItems[0].texto}
											position="above"
										/>
									</Box>
								)}
								{sabiasQueAmeyokoItems[1] && (
									<Box
										sx={{
											position: "absolute",
											top: isSmallScreen ? 55 : 35,
											left: "33%",
										}}
									>
										<SabiasQue
											icono={sabiasQueAmeyokoItems[1].icono}
											texto={sabiasQueAmeyokoItems[1].texto}
											position="above"
										/>
									</Box>
								)}
							</>
						)}
						{uenoSticker2 && !uenoSticker2Error && (
							<img
								src={uenoSticker2}
								alt=""
								onError={() => setUenoSticker2Error(true)}
								style={{
									width: 110,
									position: "absolute",
									bottom: -10,
									left: -20,
									zIndex: 2,
								}}
							/>
						)}
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

Template52.thumbnail = "/images/template-1.png";

export default Template52;
